import { useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { 
  ClipboardList, 
  Clock, 
  CheckCircle, 
  XCircle, 
  Package,
  AlertCircle,
  FileText
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { LoadingSpinner } from '@/components/ui/loading-spinner'
import { AssignmentApprovalModal } from '@/components/modals/AssignmentApprovalModal'
import { apiClient } from '@/lib/api-client'
import { useAuth } from '@/hooks/useAuth'
import { getRelativeTime } from '@shared/utils'

export function EmployeeAssignmentsPage() {
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const [selectedAssignment, setSelectedAssignment] = useState<any>(null)
  const [isApprovalModalOpen, setIsApprovalModalOpen] = useState(false)

  const { data: assignments, isLoading } = useQuery({
    queryKey: ['employee-assignments', user?.id],
    queryFn: async () => {
      const response = await apiClient.get('/assignments', {
        params: { employeeId: user?.id }
      })
      return response.data
    }
  })

  const handleApprove = (assignment: any) => {
    if (assignment && assignment.device) {
      setSelectedAssignment(assignment)
      setIsApprovalModalOpen(true)
    } else {
      console.error('Invalid assignment data:', assignment)
    }
  }

  const handleApprovalSuccess = () => {
    queryClient.invalidateQueries({ queryKey: ['employee-assignments'] })
    queryClient.invalidateQueries({ queryKey: ['assignments'] })
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  // Separate assignments by status and filter out assignments with null devices
  const validAssignments = assignments?.filter((assignment: any) => assignment && assignment.device) || []
  const byNewestAssigned = (left: any, right: any) => new Date(right.assignedOn).getTime() - new Date(left.assignedOn).getTime()
  const pendingAssignments = validAssignments.filter((assignment: any) => assignment.status === 'pending').sort(byNewestAssigned)
  const approvedAssignments = validAssignments.filter((assignment: any) => assignment.status === 'approved').sort(byNewestAssigned)
  const rejectedAssignments = validAssignments.filter((assignment: any) => assignment.status === 'rejected').sort(byNewestAssigned)
  const returnedAssignments = validAssignments
    .filter((assignment: any) => assignment.status === 'returned')
    .sort((left: any, right: any) => new Date(right.returnedOn || right.assignedOn).getTime() - new Date(left.returnedOn || left.assignedOn).getTime())

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300">
          <Clock className="w-3 h-3 mr-1" />
          Pending Approval
        </Badge>
      case 'approved':
        return <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">
          <CheckCircle className="w-3 h-3 mr-1" />
          Approved
        </Badge>
      case 'rejected':
        return <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300">
          <XCircle className="w-3 h-3 mr-1" />
          Rejected
        </Badge>
      case 'returned':
        return <Badge variant="outline" className="bg-gray-100 text-gray-800 border-gray-300">
          <Package className="w-3 h-3 mr-1" />
          Returned
        </Badge>
      default:
        return <Badge variant="outline">{status}</Badge>
    }
  }

  const AssignmentCard = ({ assignment, showActions = false }: { assignment: any; showActions?: boolean }) => (
    <Card key={assignment.id} className="hover:shadow-lg transition-shadow">
      <CardContent className="pt-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <Package className="h-6 w-6 text-primary" />
            </div>
            <div className="flex-1">
              <h3 className="font-semibold text-lg">{assignment.device?.name}</h3>
              <p className="text-sm text-muted-foreground">
                {assignment.device?.description}
              </p>
              <p className="text-xs text-muted-foreground mt-1">
                Device ID: {assignment.device?.id}
              </p>
              {assignment.notes && (
                <p className="text-xs text-muted-foreground mt-1">
                  <strong>Admin Notes:</strong> {assignment.notes}
                </p>
              )}
              {assignment.approvalNotes && (
                <p className="text-xs text-muted-foreground mt-1">
                  <strong>Your Notes:</strong> {assignment.approvalNotes}
                </p>
              )}
            </div>
          </div>
          <div className="text-right space-y-2">
            {getStatusBadge(assignment.status)}
            <div className="text-sm text-muted-foreground">
              Assigned: {getRelativeTime(assignment.assignedOn)}
            </div>
            {assignment.approvedOn && (
              <div className="text-sm text-muted-foreground">
                Approved: {getRelativeTime(assignment.approvedOn)}
              </div>
            )}
            {assignment.returnedOn && (
              <div className="text-sm text-muted-foreground">
                Returned: {getRelativeTime(assignment.returnedOn)}
              </div>
            )}
            {showActions && assignment.status === 'pending' && (
              <Button
                onClick={() => handleApprove(assignment)}
                className="mt-2"
                size="sm"
              >
                <FileText className="w-4 h-4 mr-2" />
                Review & Approve
              </Button>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  )

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <h1 className="text-xl font-semibold tracking-tight">My Assignments</h1>
        <Button asChild>
          <Link to="/requests">Request a device</Link>
        </Button>
      </div>

      {/* Pending Approvals - Show First */}
      {pendingAssignments.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <AlertCircle className="h-5 w-5 text-yellow-600" />
            <h2 className="text-xl font-semibold">Pending Your Approval</h2>
            <Badge variant="outline" className="bg-yellow-100 text-yellow-800">
              {pendingAssignments.length}
            </Badge>
          </div>
          <div className="space-y-4">
            {pendingAssignments.map((assignment: any) => (
              <AssignmentCard key={assignment.id} assignment={assignment} showActions={true} />
            ))}
          </div>
        </div>
      )}

      {/* Approved Assignments */}
      {approvedAssignments.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <h2 className="text-xl font-semibold">Approved Assignments</h2>
            <Badge variant="outline" className="bg-green-100 text-green-800">
              {approvedAssignments.length}
            </Badge>
          </div>
          <div className="space-y-4">
            {approvedAssignments.map((assignment: any) => (
              <AssignmentCard key={assignment.id} assignment={assignment} />
            ))}
          </div>
        </div>
      )}

      {/* Rejected Assignments */}
      {rejectedAssignments.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <XCircle className="h-5 w-5 text-red-600" />
            <h2 className="text-xl font-semibold">Rejected Assignments</h2>
            <Badge variant="outline" className="bg-red-100 text-red-800">
              {rejectedAssignments.length}
            </Badge>
          </div>
          <div className="space-y-4">
            {rejectedAssignments.map((assignment: any) => (
              <AssignmentCard key={assignment.id} assignment={assignment} />
            ))}
          </div>
        </div>
      )}

      {/* Returned Assignments */}
      {returnedAssignments.length > 0 && (
        <div className="space-y-4">
          <div className="flex items-center space-x-2">
            <Package className="h-5 w-5 text-gray-600" />
            <h2 className="text-xl font-semibold">Returned Assignments</h2>
            <Badge variant="outline" className="bg-gray-100 text-gray-800">
              {returnedAssignments.length}
            </Badge>
          </div>
          <div className="space-y-4">
            {returnedAssignments.map((assignment: any) => (
              <AssignmentCard key={assignment.id} assignment={assignment} />
            ))}
          </div>
        </div>
      )}

      {/* No Assignments */}
      {validAssignments.length === 0 && (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ClipboardList className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">No assignments found</h3>
            <p className="text-muted-foreground text-center">
              You don't have any device assignments yet.
            </p>
          </CardContent>
        </Card>
      )}

      {/* Approval Modal */}
      {selectedAssignment && (
        <AssignmentApprovalModal
          isOpen={isApprovalModalOpen}
          onClose={() => {
            setIsApprovalModalOpen(false)
            setSelectedAssignment(null)
          }}
          assignment={selectedAssignment}
          onSuccess={handleApprovalSuccess}
        />
      )}
    </div>
  )
}
